import { useMemo } from 'react';
import { Link } from 'react-router-dom';
import useTable from '@/hooks/useTable';
import SponsorSlot from '@/components/feature/SponsorSlot';
import { ErrorBlock, LoadingBlock } from '@/components/feature/SectionState';
import { formatPublished } from '@/lib/format';
import type { GsStory } from '@/lib/types';

/**
 * One block per coverage pillar, in the style of section-led golf news homepages:
 * a lead story with image, a short dated list, a presenting-sponsor slot and "View more".
 */
const PILLARS: { title: string; blurb: string; categories: string[] }[] = [
  { title: 'Adaptive Golf', blurb: 'Clinics, coaches and players opening the game to everyone.', categories: ['Adaptive Golf'] },
  { title: "Women's Golf", blurb: 'Leagues, championships and the women who run SoCal golf.', categories: ["Women's Golf"] },
  { title: 'Junior Golf', blurb: 'Growing the person, not just the player.', categories: ['Junior Golf'] },
  { title: 'Community & Charity', blurb: 'The tournaments, causes and people behind local golf.', categories: ['Community', 'Charity Spotlight'] },
];

function byNewest(a: GsStory, b: GsStory) {
  return new Date(b.published_at || 0).getTime() - new Date(a.published_at || 0).getTime();
}

function Meta({ story }: { story: GsStory }) {
  const date = formatPublished(story.published_at);
  const parts = [story.author, date].filter(Boolean);
  if (parts.length === 0) return null;
  return <p className="mt-1.5 font-label uppercase tracking-widest text-[10px] text-foreground-500">{parts.join(' · ')}</p>;
}

export default function PillarBlocks() {
  const { data: stories, loading, error, reload } = useTable<GsStory>('gs_stories');

  const blocks = useMemo(
    () => PILLARS.map((p) => ({ ...p, stories: stories.filter((s) => p.categories.includes(s.category)).sort(byNewest).slice(0, 4) })),
    [stories],
  );

  return (
    <section id="community" className="bg-background-50 px-4 md:px-8 py-16 md:py-24">
      <p className="font-label uppercase tracking-[0.28em] text-xs text-primary-600 font-semibold">Your golf community</p>
      <h2 className="font-heading font-bold text-4xl md:text-6xl text-foreground-950 leading-[1.02] mt-3 mb-10">Four pillars, one community</h2>

      {loading && <LoadingBlock label="Loading stories" />}
      {error && <ErrorBlock message={error} onRetry={reload} />}

      {!loading && !error && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-x-10 gap-y-14">
          {blocks.map((block) => {
            const [lead, ...rest] = block.stories;
            return (
              <article key={block.title} className="min-w-0 flex flex-col">
                <div className="flex flex-wrap items-end justify-between gap-3 border-b-2 border-foreground-950 pb-3">
                  <div className="min-w-0">
                    <h3 className="font-heading font-bold text-2xl md:text-3xl text-foreground-950">{block.title}</h3>
                    <p className="text-sm text-foreground-600 mt-1">{block.blurb}</p>
                  </div>
                  <Link
                    to={`/news?category=${encodeURIComponent(block.categories[0])}`}
                    className="inline-flex items-center gap-1.5 font-label uppercase tracking-widest text-xs font-semibold text-primary-600 hover:text-primary-700 whitespace-nowrap cursor-pointer"
                  >
                    View more<span className="w-4 h-4 flex items-center justify-center"><i className="ri-arrow-right-line"></i></span>
                  </Link>
                </div>

                <div className="mt-4">
                  <SponsorSlot label={`${block.title} presented by`} />
                </div>

                {lead ? (
                  <Link to={lead.slug ? `/news/${lead.slug}` : '/news'} className="group mt-5 flex gap-4 cursor-pointer items-start">
                    <div className="w-32 h-24 md:w-40 md:h-28 shrink-0 overflow-hidden rounded-md">
                      <img src={lead.image} alt={lead.title} className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-700" />
                    </div>
                    <div className="min-w-0">
                      <h4 className="font-heading font-bold text-lg md:text-xl leading-snug text-foreground-950 group-hover:text-primary-600 transition-colors">{lead.title}</h4>
                      <Meta story={lead} />
                    </div>
                  </Link>
                ) : (
                  <p className="mt-5 text-foreground-600">Stories for this pillar are coming soon.</p>
                )}

                {rest.length > 0 && (
                  <ul className="mt-4 border-t border-background-300">
                    {rest.map((s) => (
                      <li key={s.id} className="border-b border-background-300">
                        <Link to={s.slug ? `/news/${s.slug}` : '/news'} className="group block py-3 cursor-pointer">
                          <span className="font-heading font-bold text-base md:text-lg leading-snug text-foreground-950 group-hover:text-primary-600 transition-colors">{s.title}</span>
                          <Meta story={s} />
                        </Link>
                      </li>
                    ))}
                  </ul>
                )}
              </article>
            );
          })}
        </div>
      )}
    </section>
  );
}
